import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useWindows } from '../../contexts/WindowContext';

const MobileWindowContainer = styled.div<{ isMinimized: boolean; zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.98);
  z-index: ${props => props.zIndex};
  display: ${props => props.isMinimized ? 'none' : 'flex'};
  flex-direction: column;
`;

const MobileWindowHeader = styled.div`
  height: 50px;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  font-family: 'Ubuntu', sans-serif;
  flex-shrink: 0;
`;

const WindowTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  color: #00ffff;
  font-weight: 500;
`;

const BackButton = styled.button`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid #ff0000;
  color: #ff0000;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &:active {
    background: rgba(255, 0, 0, 0.4);
    transform: scale(0.95);
  }
`;

const MobileWindowContent = styled.div`
  flex: 1;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.95);
  position: relative;
`;

const ScrollableContent = styled.div<{ contentHeight: number }>`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  /* Dynamic padding based on content height */
  padding-bottom: ${props => props.contentHeight > window.innerHeight ? '160px' : '20px'};
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 0, 0.4);
    border-radius: 2px;
  }
`;

interface MobileWindowProps {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  isMinimized: boolean;
  zIndex: number;
}

const MobileWindow: React.FC<MobileWindowProps> = ({ 
  id, 
  title, 
  component: Component, 
  isMinimized,
  zIndex 
}) => {
  const { closeWindow } = useWindows();
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const updateContentHeight = () => {
      const content = document.querySelector(`[data-window-id="${id}"] .mobile-content`);
      if (content) {
        setContentHeight(content.scrollHeight);
      }
    };

    // Update height after component mounts and when content changes
    setTimeout(updateContentHeight, 100);
    
    // Listen for resize events
    window.addEventListener('resize', updateContentHeight);
    
    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, [id]);

  return (
    <MobileWindowContainer 
      isMinimized={isMinimized} 
      zIndex={zIndex}
      data-window-id={id}
    >
      <MobileWindowHeader>
        <WindowTitle>{title}</WindowTitle>
        <BackButton onClick={() => closeWindow(id)}>
          ← Back
        </BackButton>
      </MobileWindowHeader>
      <MobileWindowContent>
        <ScrollableContent contentHeight={contentHeight}>
          <div className="mobile-content">
            <Component />
          </div>
        </ScrollableContent>
      </MobileWindowContent>
    </MobileWindowContainer>
  );
};

export default MobileWindow;
